import { Component, Input, OnInit } from '@angular/core';
import { KeyValuePipe, NgForOf, NgIf } from '@angular/common';
import { Principal } from '../../../../shared';
import { AlertService } from '../../../../shared/alert/alert.service';
import { DatasetService } from '../../../../shared/dataset/service/dataset.service';
import { InventoryService } from '../../../../shared/inventory/service/inventory.service';
import { PlantingPreparationService } from '../../services/planting-preparation.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalConfirmComponent } from '../../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { MS_CREATE_CONFIRMED_WITHDRAWALS, VIEW_NAMES_PERMISSIONS } from '../../../../shared/auth/permissions';
import { PlantingPreparation, PlantingPreparationEntry } from '../../models/planting-preparation.model';
import { PlantingMetadata } from '../../models/planting-metadata.model';
import { PlantingRequest, WithdrawalInstruction } from '../../models/planting-request.model';
import { SearchComposite } from '../../../../shared/model/search-composite';
import { StudyEditorService } from '../../../study-editor.service';

@Component({
    selector: 'jhi-planting-preparation-modal',
    templateUrl: './planting-preparation-modal.component.html',
    styleUrl: './planting-preparation-modal.component.scss'
})
export class PlantingPreparationModalComponent implements OnInit {

    MS_CREATE_CONFIRMED_WITHDRAWALS = MS_CREATE_CONFIRMED_WITHDRAWALS;
    VIEW_NAMES_PERMISSIONS = VIEW_NAMES_PERMISSIONS;

    @Input() searchComposite: SearchComposite<any, number>;
    @Input() studyId: number;
    @Input() datasetId: number;

    entries: PlantingPreparationEntry[];
    entryDetailVariables = [];
    variableSelected;
    notesInvalid = false;
    notes: string = '';
    isCommitOnSaving = false;
    numberOfInstances = 0;
    numberOfPlots = 0;
    entryMap: Map<string, Map<string, PlantingPreparationEntry>> = new Map();
    variableMap: Map<string, Map<string, number>> = new Map();
    units: any[];

    constructor(protected principal: Principal,
                protected alertService: AlertService,
                protected datasetService: DatasetService,
                protected plantingPreparationService: PlantingPreparationService,
                protected inventoryService: InventoryService,
                protected activeModal: NgbActiveModal,
                protected modalService: NgbModal,
                protected translateService: TranslateService,
                protected studyEditorService: StudyEditorService) {
    }

    ngOnInit(): void {
        this.datasetService.getDataset(this.studyId, this.datasetId).toPromise()
            .then((response) => {
                const dataset = response.body;
                const entryDetails =
                    dataset.variables.filter((variable) => {
                        return variable && variable.dataTypeId === 1110 &&
                            variable.variableType === 'ENTRY_DETAIL' &&
                            variable.systemVariable === false;
                    });
                entryDetails.forEach((variable) => {
                    this.entryDetailVariables.push({
                        name: variable.alias ? variable.alias : variable.name, variableId: variable.termId
                    });
                });
            });

        this.plantingPreparationService.getPlantingPreparationData(this.searchComposite, this.studyId, this.datasetId).toPromise().then((data) => {
            return this.transformData(data);
        });
    }

    validateNotes() {
        const invalidChars = /[<>?|%]/;
        this.notesInvalid = invalidChars.test(this.notes);
        if (this.notesInvalid) {
            this.alertService.error('study-editor.planting-preparation.validation-notes-have-special-characters');
        }
    };

    transformData(data: PlantingPreparation) {
        return this.inventoryService.queryUnits().toPromise().then((unitTypes) => {
            const unitsById = unitTypes.reduce((unitsById, unitType) => {
                unitsById[unitType.id] = unitType.name;
                return unitsById;
            }, {});

            this.entries = data.entries;

            this.entries.forEach((entry) => {
                if (this.size(entry.stockByStockId)) {
                    // not possible to compare units, order by availableBalance at least
                    entry.stockSelected = Object.entries(entry.stockByStockId)
                        .sort((a: any, b: any) => b[1].availableBalance - a[1].availableBalance)[0][1];

                    for (const stockItem of Object.values(entry.stockByStockId)) {
                        const stock = stockItem as any;
                        if (!this.entryMap[stock.unitId]) {
                            this.entryMap[stock.unitId] = {};
                        }
                        this.entryMap[stock.unitId][entry.entryNo] = entry;
                    }

                    for (const entryDetailId of Object.keys(entry.entryDetailVariableId)) {
                        if (!this.variableMap[entryDetailId]) {
                            this.variableMap[entryDetailId] = {};
                        }
                        this.variableMap[entryDetailId][entry.entryNo] = entry.entryDetailVariableId[entryDetailId];
                    }
                }
            });
            this.units = this.entries
                .reduce((unitIds, entry) => {
                    return unitIds.concat(Object.values(entry.stockByStockId).map((stock: any) => stock.unitId));
                }, [])
                .reduce((units, unitId) => {
                    units[unitId] = { unitName: unitsById[unitId], groupTransactions: true };
                    return units;
                }, {});

            this.numberOfInstances = Object.keys(this.entries.reduce((instanceMap, entry) => {
                entry.numberOfPackets = entry.observationUnits.length;
                this.numberOfPlots += entry.observationUnits.length;
                entry.observationUnits.forEach((ou) => instanceMap[ou.instanceId] = true);
                return instanceMap;
            }, {})).length;

            if (this.size(this.units) === 0) {
                this.alertService.error('study-editor.planting-preparation.validation-no-stock-available');
            }
        });
    };

    onGroupTransactionsChecked(unitId, unit) {
        if (!unit.groupTransactions) {
            unit.withdrawAll = false;
        }
        this.revalidateEntries(unitId);
    };

    onWithdrawAllChecked(unitId, unit) {
        if (unit.withdrawAll) {
            unit.amountPerPacket = null;
            unit.useEntryDetail = false;
            unit.variableSelected = null;
        }
        this.revalidateEntries(unitId);
    };

    onUseEntryDetailChecked(unitId, unit) {
        if (unit.useEntryDetail) {
            unit.amountPerPacket = null;
            unit.withdrawAll = false;
        } else {
            unit.variableSelected = null;
        }

        if (unit.useEntryDetail && this.size(this.entryDetailVariables) === 0) {
            this.alertService.error('study-editor.planting-preparation.validation-study-must-have-one-numerical-entry-detail');
        }

        this.revalidateEntries(unitId);

    };

    onAmountPerPacketByEntryDetailChanged(unitId, unit) {
        this.revalidateEntries(unitId);
    };

    onAmountPerPacketChanged(unitId: string, unit: any) {
        this.revalidateEntries(unitId);
    };

    revalidateEntries(unitId) {
        const entryMap = this.entryMap[unitId];
        if (entryMap) {
            for (const [entryNo, entry] of Object.entries(entryMap)) {
                entryMap[Number(entryNo)].valid = this.isValid(entry);
            }
        }
    };

    stockIdChange(entry) {
        const entryMap = this.entryMap[entry.stockSelected.unitId];
        if (!entryMap) {
            return;
        }
        entryMap[entry.entryNo].valid = this.isValid(entry);
    };

    isValid(entry) {
        if (!(this.units && entry)) {
            return false;
        }
        const unit = this.units[entry.stockSelected.unitId];
        const stock = entry.stockByStockId[entry.stockSelected.stockId];
        if (!(unit && stock)) {
            return false;
        }
        if (unit.withdrawAll) {
            return stock.availableBalance > 0;
        }

        if (unit.useEntryDetail) {
            const value = unit.variableSelected ? this.variableMap[unit.variableSelected.variableId][entry.entryNo] : 0;
            return value > 0 && //
                value * entry.numberOfPackets <= stock.availableBalance;
        }

        return unit.amountPerPacket > 0 && //
            unit.amountPerPacket * entry.numberOfPackets <= stock.availableBalance;
    };

    validLotsCount(unitId) {
        const entryMap = this.entryMap[unitId];
        if (!entryMap) {
            return;
        }
        return Object.values(Object.values(entryMap))
            .filter((entry: any) => entry.valid && entry.stockSelected.unitId === Number(unitId)).length;
    };

    confirm() {
        this.validate().then((doProceed) => {
            this.plantingPreparationService.confirmPlanting(this.getPlantingRequest(), this.studyId, this.datasetId, this.isCommitOnSaving).toPromise().then(() => {
                this.activeModal.close();
                this.alertService.success('study-editor.planting-preparation.save-success');
                this.studyEditorService.triggerRefreshAndWait().then(() => {
                    this.studyEditorService.navigateToTab(this.studyId, 'inventory');
                });
            });
        });
    };

    size(obj) {
        return Object.keys(obj).length;
    };

    keys(obj) {
        return Object.keys(obj);
    };

    cancel() {
        this.activeModal.dismiss();
    };

    valid() {
        return this.entries && this.entries.some((entry) => entry.valid);
    };

    async validate() {
        const plantingNokWarning = this.translateService.instant('study-editor.planting-preparation.nok-warning');
        let confirmationMessages = this.entries.some((entry) => !entry.valid) ? [plantingNokWarning] : [];

        try {
            const metadata: PlantingMetadata = await this.plantingPreparationService.getMetadata(this.getPlantingRequest(), this.studyId, this.datasetId).toPromise();

            if (metadata.pendingTransactionsCount) {
                confirmationMessages.push(this.translateService.instant('study-editor.planting-preparation.pending-transactions-warning'));
            }
            if (metadata.confirmedTransactionsCount) {
                confirmationMessages.push(this.translateService.instant('study-editor.planting-preparation.confirmed-transactions-warning'));
            }

            if (confirmationMessages.length) {
                const proceedMessage = this.translateService.instant('study-editor.planting-preparation.proceed-message');
                const message = `<ul>${confirmationMessages.map((m) => '<li>' + m + '</li>').join('')}</ul><p>${proceedMessage}</p>`;
                const confirmModalRef = this.modalService.open(ModalConfirmComponent);
                confirmModalRef.componentInstance.message = message;
                return await confirmModalRef.result;
            }
            return true;

        } catch (err) {
            return false;
        }
    }

    getPlantingRequest(): PlantingRequest {
        const validEntries = this.entries.filter((entry) => entry.valid);
        const withdrawalsPerUnit: Map<string, WithdrawalInstruction> = Object.entries(this.units)
            .filter(([unitId, unit]) => Object.values(this.entryMap[unitId]).some((entry: any) => {
                return entry.valid && entry.stockSelected.unitId === Number(unitId);
            }))
            .reduce((withdrawalsPerUnit, [unitId, unit]) => {
                withdrawalsPerUnit[unit.unitName] = {
                    groupTransactions: unit.groupTransactions,
                    withdrawAllAvailableBalance: unit.withdrawAll,
                    withdrawUsingEntryDetail: unit.useEntryDetail,
                    entryDetailVariableId: unit.useEntryDetail ? unit.variableSelected.variableId : null,
                    withdrawalAmount: unit.amountPerPacket
                };
                return withdrawalsPerUnit;
            }, {} as Map<string, WithdrawalInstruction>);
        return {
            selectedObservationUnits: this.searchComposite,
            withdrawalsPerUnit: withdrawalsPerUnit,
            lotPerEntryNo: validEntries.map((entry) => {
                return {
                    entryNo: entry.entryNo,
                    lotId: entry.stockSelected.lotId
                };
            }),
            notes: this.notes
        };
    }

    withdrawalCalculator(unitId: number, entryNo: number, packets: number) {
        const unit = this.units[unitId];
        let amount = 0;
        if (unit) {
            if (unit.useEntryDetail) {
                amount = unit.variableSelected ? this.variableMap[unit.variableSelected.variableId][entryNo] : 0;
            } else if (unit.amountPerPacket) {
                amount = unit.amountPerPacket;
            }
        }
        return (Math.round(isNaN(amount) ? 0 : amount * 1000) * packets) / 1000;
    };

}
